import { forwardRef, type HTMLAttributes, type ReactNode, type MouseEvent } from 'react';
import styles from './Chip.module.css';

export type ChipVariant = 'default' | 'primary' | 'secondary' | 'accent';
export type ChipSize = 'sm' | 'md';

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  /** Visual style variant */
  variant?: ChipVariant;
  /** Size of the chip */
  size?: ChipSize;
  /** Whether the chip is selected/active */
  selected?: boolean;
  /** Whether to show the remove (X) button */
  removable?: boolean;
  /** Callback when the remove button is clicked */
  onRemove?: () => void;
  /** Whether the chip is disabled */
  disabled?: boolean;
  /** Optional icon to display before text */
  icon?: ReactNode;
  /** Chip contents */
  children: ReactNode;
}

/**
 * Chip component for filter chips and tags.
 * Compact size with optional remove button.
 *
 * @example
 * ```tsx
 * <Chip variant="primary" selected>
 *   Active Filter
 * </Chip>
 *
 * <Chip removable onRemove={() => console.log('removed')}>
 *   Removable
 * </Chip>
 * ```
 */
export const Chip = forwardRef<HTMLSpanElement, ChipProps>(
  (
    {
      variant = 'default',
      size = 'md',
      selected = false,
      removable = false,
      onRemove,
      disabled = false,
      icon,
      className,
      children,
      onClick,
      ...props
    },
    ref
  ) => {
    const isInteractive = !!onClick && !disabled;

    const handleRemove = (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!disabled && onRemove) {
        onRemove();
      }
    };

    const classNames = [
      styles.chip,
      styles[variant],
      styles[size],
      selected && styles.selected,
      disabled && styles.disabled,
      isInteractive && styles.interactive,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <span
        ref={ref}
        className={classNames}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        aria-pressed={isInteractive ? selected : undefined}
        aria-disabled={disabled || undefined}
        onClick={!disabled ? onClick : undefined}
        onKeyDown={
          isInteractive
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick?.(e as unknown as MouseEvent<HTMLSpanElement>);
                }
              }
            : undefined
        }
        {...props}
      >
        {icon && (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        )}
        <span className={styles.label}>{children}</span>
        {removable && (
          <button
            type="button"
            className={styles.removeButton}
            onClick={handleRemove}
            disabled={disabled}
            aria-label="Remove"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="12" y1="4" x2="4" y2="12" />
              <line x1="4" y1="4" x2="12" y2="12" />
            </svg>
          </button>
        )}
      </span>
    );
  }
);

Chip.displayName = 'Chip';
